import { ChevronDown } from 'lucide-react';
import { DeviceChangeBadge } from 'Modules/softphone/components/SoftphoneDevTools/DeviceChangeBadge';
import { EventsBadge } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsTabs/EventsTab/EventsBadge';
import { EventsDirectionIcon } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsTabs/EventsTab/EventsDirectionIcon';
import type { EventsItemProps } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsTabs/EventsTab/EventsItem/index.types';
import {
  formatTimestamp,
  getDeviceChangeType,
} from 'Modules/softphone/components/SoftphoneDevTools/utils';
import React from 'react';
import { cn } from '~/lib/utils';

/**
 * EventsItem - One line of the event timeline, expandable into its raw payload
 *
 * A row, not a card: the timeline is read as a single column of events on
 * one clock, and giving each its own border would break it into a list of
 * objects.
 *
 * A device change is badged with the device rather than with DEVICE: the
 * channel is already in the filter above and in the legend below, and which of
 * the three switched is what a reader is scanning this column for.
 */
export const EventsItem: React.FC<EventsItemProps> = ({
  event,
  isExpanded,
  onToggle,
}) => {
  const deviceType = getDeviceChangeType(event);

  return (
    <div className="border-b border-zinc-800 last:border-b-0">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isExpanded}
        className="flex w-full items-center gap-[9px] px-[11px] py-2 text-left transition-colors hover:bg-zinc-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-emerald-400/40"
      >
        {deviceType ? (
          <DeviceChangeBadge type={deviceType} />
        ) : (
          <EventsBadge type={event.type} />
        )}
        {/* A lifecycle event travelled in no direction, so it gets no chevron. */}
        {event.direction && (
          <EventsDirectionIcon direction={event.direction} />
        )}
        <span className="min-w-0 flex-1 truncate font-sp-mono text-xs text-zinc-200">
          {event.message}
        </span>
        <span className="flex-shrink-0 font-sp-mono text-[10.5px] text-zinc-400">
          {formatTimestamp(event.timestamp, { fallback: '—' })}
        </span>
        {event.details && (
          <ChevronDown
            className={cn(
              'h-3.5 w-3.5 flex-shrink-0 text-zinc-400 transition-transform',
              isExpanded && 'rotate-180'
            )}
          />
        )}
      </button>

      {isExpanded && event.details && (
        <pre className="max-h-[190px] overflow-auto border-t border-zinc-800 bg-zinc-950 px-3 py-2.5 font-sp-mono text-[11px] leading-[1.6] text-zinc-300">
          {JSON.stringify(event.details, null, 2)}
        </pre>
      )}
    </div>
  );
};
